import React, { useState, useCallback } from 'react';
import { debounce } from '../../../utils/debounce';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ProductCard from '../ProductCard/ProductCard';
import { USE_MOCK_API } from '../../../config/api';
import inventoryApi from '../../../services/api/inventoryApi';
import mockInventoryApi from '../../../services/api/mockInventoryApi';
import { trackInventorySearch } from '../../../utils/activityTracker';
import './InventorySearch.css';

// Choose API based on configuration
const api = USE_MOCK_API ? mockInventoryApi : inventoryApi;

const InventorySearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 3) {
        setProducts([]);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await api.searchProducts(term);
        
        // Handle both response formats: { products: [...] } or direct array
        const productsData = response.products || response || [];
        setProducts(productsData);
        trackInventorySearch(term);
      } catch (err) {
        setError('Erro ao buscar produtos. Tente novamente.');
        console.error('Search error:', err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [api]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleProductClick = (product) => {
    console.log('Product clicked:', product);
    // TODO: Implement product detail view or action
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    debouncedSearch(searchTerm);
  };

  return (
    <div className="inventory-search-container">
      <div className="search-panel">
        <h1>Consulta de Estoque</h1>
        <form onSubmit={handleSubmit} className="search-form">
          <div className="form-group">
            <label htmlFor="searchInput">Código do Produto:</label>
            <input
              type="text"
              id="searchInput"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Digite para buscar..."
              minLength={3}
              required
              autoComplete="off"
            />
            <button type="submit" disabled={loading}>
              Consultar
            </button>
          </div>
        </form>
        
        {/* Development indicator */}
        {USE_MOCK_API && (
          <div className="dev-indicator">
            <small>🔧 Usando dados de teste (Mock API)</small>
          </div>
        )}
      </div>

      <div className="results-panel">
        <h2>Resultados da Busca</h2>
        <div className="search-results">
          {loading && (
            <div className="loading-container">
              <LoadingSpinner text="Buscando produtos..." />
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {!loading && !error && products.length > 0 && (
            <div className="products-list">
              {products.map((product, index) => (
                <ProductCard
                  key={`${product.codigo}-${index}`}
                  product={product}
                  onClick={handleProductClick}
                />
              ))}
            </div>
          )}

          {!loading && !error && searchTerm.length >= 3 && products.length === 0 && (
            <div className="empty-state">
              Nenhum produto encontrado
            </div>
          )}

          {!loading && !error && searchTerm.length < 3 && (
            <div className="empty-state">
              Digite algo para buscar produtos...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventorySearch;
